import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Car, NavigationArrow, MapPin, ArrowUpRight } from "@phosphor-icons/react";
import { useContent } from "@/context/ContentContext";

const ADDRESS = "Rua Eduardina de Miranda Telles, 250, Piabetá, Magé - RJ, 25931-774";
const PLACE_ID = "ChIJIVzxgU8LmQARHUP9RkalQOM";
const COORDS = "-22.6113102,-43.1768641";
// Official Google Meu Negócio (verified profile) link
const ROUTE_URL = "https://maps.google.com/maps?cid=16375269966376420125";
const MAP_EMBED = `https://maps.google.com/maps?q=${COORDS}&z=17&hl=pt-BR&output=embed`;

const routeTo = (dest) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;

const PARKINGS = [
  {
    label: "Estacionamento Boa Sorte",
    hint: "R. Artur Rodrigues Loivos, 108 — logo ao lado",
    url: routeTo("Estacionamento Boa Sorte, R. Artur Rodrigues Loivos, 108, Piabetá, Magé - RJ"),
  },
  {
    label: "Estacionamento Rio Sul",
    hint: "R. Artur Rodrigues Loivos, 192 — em frente",
    url: routeTo("Estacionamento Rio Sul, R. Artur Rodrigues Loivos, 192, Piabetá, Magé - RJ"),
  },
];

export default function Estacionamento() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { parking_text } = useContent();

  return (
    <section id="estacionamento" ref={ref} className="relative bg-vm-bg py-24 md:py-32" data-testid="parking-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div>
          <motion.p initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ duration: 0.6 }} className="text-xs tracking-[0.4em] uppercase text-vm-gold mb-6">
            Localização & Estacionamento
          </motion.p>
          <motion.h2 initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }} className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="parking-title">
            Chegue com <span className="gold-text italic">Tranquilidade</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.15 }} className="mt-6 text-lg text-vm-mutedFg max-w-lg leading-relaxed" data-testid="parking-text">
            {parking_text}
          </motion.p>

          <div className="mt-8 flex items-start gap-3 text-vm-mutedFg">
            <MapPin size={22} weight="duotone" className="text-vm-gold shrink-0 mt-0.5" />
            <span className="text-sm leading-relaxed">Rua Eduardina de Miranda Telles, 250 — 1º e 2º Piso, Piabetá, Magé - RJ · CEP 25931-774</span>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
            <a href={ROUTE_URL} target="_blank" rel="noopener noreferrer" className="btn-sweep relative z-0 inline-flex items-center justify-center gap-2 bg-vm-gold text-vm-bg hover:text-vm-bg font-semibold px-6 py-3.5 rounded-full" data-testid="route-btn">
              <NavigationArrow size={18} weight="fill" /> Como chegar (rota)
            </a>
          </div>

          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {PARKINGS.map((p, i) => (
              <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" className="group rounded-xl border border-vm-border bg-vm-surface p-4 hover:border-vm-gold/40 transition-colors duration-500 flex items-start gap-3" data-testid={`parking-link-${i}`}>
                <Car size={22} weight="duotone" className="text-vm-gold shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium flex items-center gap-1">{p.label} <ArrowUpRight size={14} className="text-vm-mutedFg group-hover:text-vm-gold transition-colors" /></p>
                  <p className="text-xs text-vm-mutedFg mt-0.5">{p.hint}</p>
                </div>
              </a>
            ))}
          </div>
        </div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={inView ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="relative overflow-hidden rounded-2xl border border-vm-border h-[360px] lg:h-[460px]">
          <iframe
            title="Mapa VM Piabetá"
            src={MAP_EMBED}
            className="w-full h-full grayscale contrast-125 opacity-90"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="parking-map"
          />
          <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-vm-gold/10 rounded-2xl" />
        </motion.div>
      </div>
    </section>
  );
}
